import { z } from "zod"

import { GlTransactionRepeatByUnit, GlTransactionRowType } from "@/graphql/codegen/graphql"

const transactionLineSchema = z.object({
  glTransactionRowId: z.string().optional(),
  type: z.nativeEnum(GlTransactionRowType, { required_error: "Type is required" }),
  glAccountId: z
    .string()
    .nullable()
    .refine((val) => val !== null && val.length > 0, "Account is required"),
  amount: z
    .number({ invalid_type_error: "Amount is required" })
    .nullable()
    .refine((val) => val !== null && val > 0, "Amount must be greater than 0")
    .refine((val) => val === null || val <= 999999999.99, "Amount must not exceed 999,999,999.99"),
})

export const glTransactionFormSchema = z
  .object({
    transactionLines: z.array(transactionLineSchema).min(1),
    transactionDate: z.string().min(1, "Transaction Date is required"),
    description: z.string().min(1, "Description is required").max(30, "Description must not exceed 30 characters"),
    note: z.string().max(100, "Note must not exceed 100 characters").optional(),
    isRecurring: z.boolean().default(false),
    isExistingRecurring: z.boolean().default(false),
    repeatEndDate: z.string().optional(),
    repeatEvery: z.number().int().min(1).optional(),
    repeatByUnit: z.nativeEnum(GlTransactionRepeatByUnit).optional(),
  })
  .superRefine((values, ctx) => {
    const filledLines = values.transactionLines.filter(
      (line) => line.glAccountId && line.amount !== null && line.amount > 0
    )
    const debitLines = filledLines.filter((line) => line.type === GlTransactionRowType.Debit)
    const creditLines = filledLines.filter((line) => line.type === GlTransactionRowType.Credit)

    if (!debitLines.length || !creditLines.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "At least one debit and one credit line are required",
        path: ["transactionLines"],
      })
    }

    const totalDebits = debitLines.reduce((acc, line) => acc + (line.amount ?? 0), 0)
    const totalCredits = creditLines.reduce((acc, line) => acc + (line.amount ?? 0), 0)

    if (debitLines.length > 0 && creditLines.length > 0 && Math.abs(totalDebits - totalCredits) > 0.001) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Total debits must equal total credits",
        path: ["transactionLines"],
      })
    }

    if (values.isRecurring && !values.isExistingRecurring) {
      if (!values.repeatEndDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Transaction End Date is required",
          path: ["repeatEndDate"],
        })
      }

      if (!values.repeatEvery) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Repeat Every is required",
          path: ["repeatEvery"],
        })
      }

      if (!values.repeatByUnit) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Repeat By is required",
          path: ["repeatByUnit"],
        })
      }

      if (values.repeatEndDate && values.transactionDate && values.repeatEndDate <= values.transactionDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "End date must be after start date for recurring entries",
          path: ["repeatEndDate"],
        })
      }
    }
  })

export type GlTransactionFormValues = z.infer<typeof glTransactionFormSchema>

export type GlTransactionLineFormValues = z.infer<typeof transactionLineSchema>
